import { Link } from 'react-router';
import { SEO, BreadcrumbSchema, FAQSchema, ServiceSchema, SpeakableSchema } from '@/components/seo/seo';

function RatingBadge({ rating, size = 'md' }: { rating: 'red' | 'amber' | 'green'; size?: 'sm' | 'md' | 'lg' }) {
  const colors = {
    red: 'bg-rose-500 text-white',
    amber: 'bg-amber-500 text-white',
    green: 'bg-emerald-500 text-white',
  };
  const sizes = {
    sm: 'px-2 py-0.5 text-xs',
    md: 'px-3 py-1 text-sm',
    lg: 'px-4 py-2 text-base',
  };
  return (
    <span className={`inline-block font-bold rounded-full uppercase ${colors[rating]} ${sizes[size]}`}>
      {rating}
    </span>
  );
}

function QuoteCard({ quote, source }: { quote: string; source: string }) {
  return (
    <blockquote className="bg-surface-50 border-l-4 border-brand-500 p-6 rounded-r-xl">
      <p className="text-surface-800 italic">"{quote}"</p>
      <cite className="text-sm text-surface-600 mt-2 block not-italic">— {source}</cite>
    </blockquote>
  );
}

export function CityOfLondonCouncilPage() {
  const faqItems = [
    {
      question: 'The City of London spends over £1.2m a year on highways — can I still claim?',
      answer: 'Yes. The DfT Spend scorecard is GREEN, but your claim turns on whether the specific defect that damaged your vehicle was reasonably inspected and repaired under Section 58 — not on aggregate spend across the Square Mile. The DfT Condition scorecard is AMBER, and the Corporation\'s own Coarse Visual Inspection data shows 18% of streets in Red priority in 2024, with a five-year average of 21%.',
    },
    {
      question: 'My pothole was on a red route — is the City Corporation responsible?',
      answer: 'Probably not. The City of London Corporation is the highway authority for all streets in the Square Mile except four TfL Red Routes, for which Transport for London is responsible. Check whether your incident was on a borough-maintained street or a TfL red route before you claim. The Corporation\'s transparency report covers only the streets it maintains.',
    },
    {
      question: 'The council does not publish pothole fill counts — does that matter?',
      answer: 'It can. Unlike most authorities, the Corporation does not estimate how many potholes it has filled in each of the last five years. It instead links to live pothole data. That absence does not block a claim, but it means your case should rest on photos of the specific defect, prior reports via the Corporation\'s fault-reporting system, and its published condition and spend data — not borough-wide pothole totals.',
    },
    {
      question: 'Does the Corporation\'s admission on utility works help my claim?',
      answer: 'It can. The Corporation states that third-party utility interventions are "inevitable and frequent in the City" and can "demonstrably shorten or compromise the structural life cycle of the highway by a number of years." If your defect formed after utility excavations on or near your route, that is documented knowledge of an elevated risk — which raises what a reasonable inspection and repair regime must look like on that street.',
    },
    {
      question: 'Condition improved — Red streets fell from 24% to 18%. Does that weaken my claim?',
      answer: 'Not automatically. The Corporation\'s own analysis still describes a "steady state" where roughly a quarter of the network has been renewed and at least a quarter remains in categories warranting resurfacing or renewal. Your claim turns on the specific defect: its size, age, prior reports and whether it sat in a Red-rated section at the last annual Coarse Visual Inspection.',
    },
    {
      question: 'What does the DfT AMBER best-practice rating mean for my claim?',
      answer: 'The Corporation earns GREEN on spend and AMBER on condition and best practice. DfT scoring shows its weakest condition metric on unclassified roads (39.0 on the condition index). Expect a structured Section 58 defence backed by annual CVI surveys and a documented life-cycle programme — but utility disruption, reactive "make safes" and the Corporation\'s own resurfacing backlog still give grounds to challenge whether your specific defect was caught in time.',
    },
  ];

  return (
    <div className="min-h-screen">
      <SEO
        title="City of London Pothole Claims | Quarter of Square Mile Needs Resurfacing | Fixtyer"
        description="City of London Corporation: 18% of Square Mile streets in Red condition, other highway funding streams hit £494k in 2024/25, and at least a quarter of the network needs resurfacing. What the DfT AMBER rating means for your claim."
        canonical="/councils/city-of-london"
      />
      <BreadcrumbSchema
        items={[
          { name: 'Home', url: '/' },
          { name: 'Councils', url: '/councils' },
          { name: 'City of London', url: '/councils/city-of-london' },
        ]}
      />
      <FAQSchema faqs={faqItems} />
      <ServiceSchema
        councilName="City of London Corporation"
        councilSlug="city-of-london"
        rating="amber"
      />
      <SpeakableSchema
        url="/councils/city-of-london"
        headline="City of London Road Maintenance Report"
        cssSelectors={['[data-speakable="summary"]', 'h1', 'h2']}
      />

      {/* Hero */}
      <section className="relative bg-brand-950 text-white overflow-hidden">
        {/* Top chevrons */}
        <div
          className="absolute top-0 left-0 right-0 h-8 z-10"
          style={{
            background: `repeating-linear-gradient(
              -45deg,
              #f97316,
              #f97316 40px,
              #1e40af 40px,
              #1e40af 80px
            )`,
          }}
        />

        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 pt-20 pb-16">
          <nav className="text-sm text-brand-400 mb-8 flex items-center gap-2">
            <Link to="/" className="hover:text-white transition-colors">Home</Link>
            <span>/</span>
            <Link to="/councils" className="hover:text-white transition-colors">Councils</Link>
            <span>/</span>
            <span className="text-white">City of London</span>
          </nav>

          <div className="flex flex-wrap items-center gap-3 mb-4">
            <RatingBadge rating="amber" size="lg" />
            <span className="text-brand-300">Overall</span>
            <span className="text-brand-500">|</span>
            <span className="flex items-center gap-2">
              <RatingBadge rating="amber" size="sm" /> <span className="text-brand-300 text-sm">Condition</span>
            </span>
            <span className="flex items-center gap-2">
              <RatingBadge rating="green" size="sm" /> <span className="text-brand-300 text-sm">Spend</span>
            </span>
            <span className="flex items-center gap-2">
              <RatingBadge rating="amber" size="sm" /> <span className="text-brand-300 text-sm">Best Practice</span>
            </span>
          </div>

          <h1 className="font-display text-4xl sm:text-5xl font-bold leading-tight mb-6">
            A Quarter of the Square Mile Needs Resurfacing
          </h1>

          <p className="text-xl text-brand-200 max-w-2xl mb-8 leading-relaxed" data-speakable="summary">
            Britain's smallest highway network is also one of its busiest. The City of London Corporation
            earns <strong className="text-emerald-400">GREEN</strong> for spend yet rates{' '}
            <strong className="text-amber-400">AMBER</strong> overall — because its own annual inspection
            data shows <strong className="text-rose-400">18% of streets in Red priority</strong> in 2024,
            other funding streams hit <strong className="text-rose-400">£494k in 2024/25</strong>, and the
            Corporation admits <strong className="text-amber-400">at least a quarter of the network</strong>{' '}
            is in a category warranting resurfacing or renewal on the Square Mile's intensely trafficked streets.
          </p>

          {/* Key stat callout */}
          <div className="bg-amber-500/20 border border-amber-500/30 rounded-2xl p-6 max-w-md">
            <div className="text-6xl font-bold text-amber-400">£494k</div>
            <div className="text-white font-medium mt-2">Other funding streams in 2024/25</div>
            <div className="text-brand-300 text-sm mt-1">
              Up from £122k the year before — part of £1.274m total highway spend on a network the Corporation
              itself says sees utility works "inevitable and frequent," shortening road life by years.
            </div>
          </div>
        </div>

        {/* Bottom chevrons */}
        <div
          className="absolute bottom-0 left-0 right-0 h-8 z-10"
          style={{
            background: `repeating-linear-gradient(
              -45deg,
              #1e40af,
              #1e40af 40px,
              #f97316 40px,
              #f97316 80px
            )`,
          }}
        />
      </section>

      {/* The Square Mile context */}
      <section className="py-16 bg-white">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-display text-3xl font-bold text-brand-950 mb-4">
            The Smallest Network, The Heaviest Load
          </h2>
          <p className="text-surface-600 mb-8">
            What makes City of London claims different from any other London borough
          </p>

          <div className="grid md:grid-cols-2 gap-6 mb-8">
            <div className="bg-surface-50 rounded-xl p-6 border border-surface-200">
              <h3 className="font-semibold text-brand-900 mb-3">Highway authority — with exceptions</h3>
              <p className="text-surface-600 text-sm mb-3">
                The City of London Corporation is the highway authority for all streets in the Square Mile
                except four TfL Red Routes, for which Transport for London is responsible. The Operations
                Department covers highway maintenance, drainage, resurfacing, street lighting and cleansing.
              </p>
              <p className="text-surface-600 text-sm">
                If your pothole was on a red route, your claim is against TfL — not the Corporation. For
                every other street in EC1–EC4, EC2, EC3 and EC4, the Corporation is the correct defendant.
              </p>
            </div>
            <div className="bg-brand-950 text-white rounded-xl p-6">
              <h3 className="font-semibold text-brand-300 mb-3">DfT allocation vs actual spend</h3>
              <p className="text-brand-200 text-sm mb-3">
                The Department for Transport allocated £70,000 in additional highway maintenance funding
                to the Corporation. Total highway spend in 2024/25 was £1.274m — resurfacing, reactive
                maintenance and other funding streams combined.
              </p>
              <p className="text-white font-medium text-sm">
                That gap between DfT allocation and Corporation spend underpins the GREEN spend scorecard —
                but it does not eliminate liability for individual defects that persisted between inspections.
              </p>
            </div>
          </div>

          <QuoteCard
            quote="In recent years, a proverbial 'steady state' has been reached, whereby approximately a quarter of the City's network has been renewed (or is at least in its early years of lifespan). That said, at least a quarter of the network (on average) is in a category enabling it to be considered for resurfacing or renewal."
            source="City of London Corporation — Highway maintenance and operations transparency report (May 2026)"
          />
        </div>
      </section>

      {/* The Condition Picture */}
      <section className="py-16 bg-surface-50">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-display text-3xl font-bold text-brand-950 mb-4">
            What The CVI Condition Data Shows
          </h2>
          <p className="text-surface-600 mb-8">
            Five years of Coarse Visual Inspection ratings from the Corporation's own transparency report
          </p>

          <div className="bg-white rounded-2xl border border-surface-200 overflow-hidden mb-8">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-surface-100 border-b border-surface-200">
                  <th className="px-4 py-3 text-left font-semibold text-brand-900">Priority rating</th>
                  <th className="px-4 py-3 text-center font-semibold text-brand-900">2020</th>
                  <th className="px-4 py-3 text-center font-semibold text-brand-900">2021</th>
                  <th className="px-4 py-3 text-center font-semibold text-brand-900">2022</th>
                  <th className="px-4 py-3 text-center font-semibold text-brand-900">2023</th>
                  <th className="px-4 py-3 text-center font-semibold text-brand-900">2024</th>
                  <th className="px-4 py-3 text-center font-semibold text-brand-900">5-yr avg</th>
                </tr>
              </thead>
              <tbody>
                <tr className="border-b border-surface-100 bg-rose-50">
                  <td className="px-4 py-3 font-medium text-rose-900">Red (5/6)</td>
                  <td className="px-4 py-3 text-center">24%</td>
                  <td className="px-4 py-3 text-center">21%</td>
                  <td className="px-4 py-3 text-center">19%</td>
                  <td className="px-4 py-3 text-center">18%</td>
                  <td className="px-4 py-3 text-center font-bold text-rose-600">18%</td>
                  <td className="px-4 py-3 text-center font-bold text-rose-600">21%</td>
                </tr>
                <tr className="border-b border-surface-100">
                  <td className="px-4 py-3 font-medium text-amber-900">Dark Amber (4)</td>
                  <td className="px-4 py-3 text-center">13%</td>
                  <td className="px-4 py-3 text-center">13%</td>
                  <td className="px-4 py-3 text-center">11%</td>
                  <td className="px-4 py-3 text-center">9%</td>
                  <td className="px-4 py-3 text-center">10%</td>
                  <td className="px-4 py-3 text-center">11%</td>
                </tr>
                <tr className="border-b border-surface-100">
                  <td className="px-4 py-3 font-medium text-amber-800">Light Amber (3)</td>
                  <td className="px-4 py-3 text-center">10%</td>
                  <td className="px-4 py-3 text-center">10%</td>
                  <td className="px-4 py-3 text-center">11%</td>
                  <td className="px-4 py-3 text-center">12%</td>
                  <td className="px-4 py-3 text-center">11%</td>
                  <td className="px-4 py-3 text-center">11%</td>
                </tr>
                <tr className="border-b border-surface-100 bg-amber-50">
                  <td className="px-4 py-3 font-medium text-amber-900">Yellow (2)</td>
                  <td className="px-4 py-3 text-center">13%</td>
                  <td className="px-4 py-3 text-center">13%</td>
                  <td className="px-4 py-3 text-center">11%</td>
                  <td className="px-4 py-3 text-center font-bold text-amber-600">19%</td>
                  <td className="px-4 py-3 text-center font-bold text-amber-600">19%</td>
                  <td className="px-4 py-3 text-center">16%</td>
                </tr>
                <tr className="border-b border-surface-100">
                  <td className="px-4 py-3 font-medium text-emerald-800">Light Green (1)</td>
                  <td className="px-4 py-3 text-center">17%</td>
                  <td className="px-4 py-3 text-center">18%</td>
                  <td className="px-4 py-3 text-center">16%</td>
                  <td className="px-4 py-3 text-center">18%</td>
                  <td className="px-4 py-3 text-center">19%</td>
                  <td className="px-4 py-3 text-center">18%</td>
                </tr>
                <tr>
                  <td className="px-4 py-3 font-medium text-emerald-900">Dark Green (0)</td>
                  <td className="px-4 py-3 text-center">23%</td>
                  <td className="px-4 py-3 text-center">25%</td>
                  <td className="px-4 py-3 text-center font-bold text-emerald-600">31%</td>
                  <td className="px-4 py-3 text-center">25%</td>
                  <td className="px-4 py-3 text-center">24%</td>
                  <td className="px-4 py-3 text-center">27%</td>
                </tr>
              </tbody>
            </table>
          </div>

          <div className="grid md:grid-cols-2 gap-6 mb-8">
            <div className="bg-rose-50 border border-rose-200 rounded-xl p-6">
              <h3 className="font-semibold text-rose-900 mb-3">Red priority — resurfacing within 12–24 months</h3>
              <p className="text-rose-800 text-sm mb-3">
                The Corporation defines Red (5/6) streets as showing "many heavy signs of deterioration
                and resurfacing should be programmed within the next 12–24 months." At 18% in 2024,
                nearly one in five Square Mile streets sits in that category — down from 24% in 2020,
                but still a five-year average of 21%.
              </p>
              <p className="text-rose-800 text-sm">
                Dark Amber (4) adds another 10% in 2024 — streets where resurfacing should be considered
                within three to five years. Combined, roughly 28% of the network is in the two highest
                deterioration bands.
              </p>
            </div>
            <div className="bg-amber-50 border border-amber-200 rounded-xl p-6">
              <h3 className="font-semibold text-amber-900 mb-3">The Yellow category shift</h3>
              <p className="text-amber-800 text-sm mb-3">
                Yellow (2) — streets showing "little or no signs of deterioration" — jumped from 11% in
                2022 to 19% in both 2023 and 2024. Meanwhile Dark Green (0), recently resurfaced streets,
                fell from a peak of 31% in 2022 to 24% in 2024.
              </p>
              <p className="text-amber-800 text-sm">
                That pattern is consistent with a network ageing out of its post-resurfacing window —
                fewer freshly renewed streets, more sections drifting toward maintenance need. Figures are
                based on an internal Coarse Visual Inspection survey, carried out annually.
              </p>
            </div>
          </div>

          <div className="bg-brand-950 text-white rounded-2xl p-8">
            <h3 className="font-display text-xl font-bold mb-4">DfT condition scorecard — AMBER</h3>
            <div className="grid sm:grid-cols-3 gap-6 mb-6">
              <div>
                <div className="text-3xl font-bold text-amber-400">64.5</div>
                <div className="text-brand-300 text-sm">A-road condition index</div>
              </div>
              <div>
                <div className="text-3xl font-bold text-amber-400">66.0</div>
                <div className="text-brand-300 text-sm">B/C-road condition index</div>
              </div>
              <div>
                <div className="text-3xl font-bold text-rose-400">39.0</div>
                <div className="text-brand-300 text-sm">U-road condition index</div>
              </div>
            </div>
            <p className="text-brand-200">
              DfT rates the Corporation <strong className="text-amber-400">AMBER on condition</strong> overall.
              The weakest score is on unclassified roads — the category where most Square Mile side streets sit.
              Your claim should reference the CVI priority band for your specific street, not borough-wide averages alone.
            </p>
          </div>
        </div>
      </section>

      {/* Spending */}
      <section className="py-16 bg-white">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-display text-3xl font-bold text-brand-950 mb-4">
            £1.274m Spent — Other Streams Quadrupled
          </h2>
          <p className="text-surface-600 mb-8">
            Five years of highway maintenance spend from the Corporation's transparency report
          </p>

          <div className="bg-white rounded-2xl border border-surface-200 overflow-hidden mb-8">
            <table className="w-full">
              <thead>
                <tr className="bg-surface-100 border-b border-surface-200">
                  <th className="px-6 py-3 text-left text-sm font-semibold text-brand-900">Financial year</th>
                  <th className="px-6 py-3 text-center text-sm font-semibold text-brand-900">Resurfacing</th>
                  <th className="px-6 py-3 text-center text-sm font-semibold text-brand-900">Reactive maintenance</th>
                  <th className="px-6 py-3 text-center text-sm font-semibold text-brand-900">Other funding streams</th>
                  <th className="px-6 py-3 text-center text-sm font-semibold text-brand-900">Total</th>
                </tr>
              </thead>
              <tbody className="text-sm">
                <tr className="border-b border-surface-100">
                  <td className="px-6 py-4 font-mono text-brand-900">2025/26*</td>
                  <td className="px-6 py-4 text-center">£601k</td>
                  <td className="px-6 py-4 text-center">£80k</td>
                  <td className="px-6 py-4 text-center">£193k</td>
                  <td className="px-6 py-4 text-center font-bold">£874k</td>
                </tr>
                <tr className="border-b border-surface-100 bg-rose-50">
                  <td className="px-6 py-4 font-mono text-brand-900">2024/25</td>
                  <td className="px-6 py-4 text-center">£700k</td>
                  <td className="px-6 py-4 text-center">£80k</td>
                  <td className="px-6 py-4 text-center font-bold text-rose-600">£494k</td>
                  <td className="px-6 py-4 text-center font-bold text-rose-600">£1.274m</td>
                </tr>
                <tr className="border-b border-surface-100">
                  <td className="px-6 py-4 font-mono text-brand-900">2023/24</td>
                  <td className="px-6 py-4 text-center">£720k</td>
                  <td className="px-6 py-4 text-center">£80k</td>
                  <td className="px-6 py-4 text-center">£122k</td>
                  <td className="px-6 py-4 text-center font-bold">£922k</td>
                </tr>
                <tr className="border-b border-surface-100">
                  <td className="px-6 py-4 font-mono text-brand-900">2022/23</td>
                  <td className="px-6 py-4 text-center">£700k</td>
                  <td className="px-6 py-4 text-center">£80k</td>
                  <td className="px-6 py-4 text-center">£126k</td>
                  <td className="px-6 py-4 text-center font-bold">£926k</td>
                </tr>
                <tr className="border-b border-surface-100">
                  <td className="px-6 py-4 font-mono text-brand-900">2021/22</td>
                  <td className="px-6 py-4 text-center">£580k</td>
                  <td className="px-6 py-4 text-center">£150k</td>
                  <td className="px-6 py-4 text-center">£101k</td>
                  <td className="px-6 py-4 text-center font-bold">£761k</td>
                </tr>
              </tbody>
            </table>
          </div>

          <div className="grid md:grid-cols-2 gap-6">
            <div className="bg-surface-50 rounded-xl p-6 border border-surface-200">
              <h3 className="font-semibold text-brand-900 mb-3">GREEN spend — but reactive pressure rising</h3>
              <p className="text-surface-600 text-sm mb-3">
                DfT gives the Corporation a GREEN Spend scorecard (capital spend index 91.6, preventative
                spend score 100.0). Resurfacing budgets run at £580k–£720k a year. But "other funding
                streams" — which the Corporation does not break down further in the published table —
                quadrupled from £122k to £494k between 2023/24 and 2024/25.
              </p>
              <p className="text-surface-600 text-sm">
                The Corporation notes capital project figures fluctuate each year with no set budget,
                depending on the volume of work undertaken. The 2025/26 total of £874k is year-to-date
                and "likely to increase with funding from other streams."
              </p>
            </div>
            <div className="bg-amber-50 border border-amber-200 rounded-xl p-6">
              <h3 className="font-semibold text-amber-900 mb-3">Make safes until resurfacing arrives</h3>
              <p className="text-amber-800 text-sm">
                The Corporation states that when a section nears the end of its life cycle, either
                "make safes" are planned until programmed works take place (if already scheduled within
                12 months) or planned works are accelerated forward. That admission matters: a pothole
                patched as a temporary make-safe is evidence the Corporation knew the carriageway was
                failing — not that it had been properly maintained.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Utility works */}
      <section className="py-16 bg-surface-50">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-display text-3xl font-bold text-brand-950 mb-4">
            Utility Works — Inevitable, Frequent, Life-Shortening
          </h2>
          <p className="text-surface-600 mb-8">
            The Corporation's own explanation for why reactive maintenance dominates on Square Mile streets
          </p>

          <div className="space-y-6 mb-8">
            <QuoteCard
              quote="Third party interventions (such as utility works) are inevitable and frequent in the City, and this can demonstrably shorten or compromise the structural life cycle of the highway by a number of years. Only after this, does reactive maintenance then become more apparent or required when needed."
              source="City of London Corporation — Highway maintenance and operations transparency report (May 2026)"
            />
            <QuoteCard
              quote="The Highways and Streetworks teams work closely in order to attempt to minimise disruption to the network. Any long-term resurfacing or scheme work is always programmed to occur after any major or substantial third-party utility works."
              source="City of London Corporation — Highway maintenance and operations transparency report (May 2026)"
            />
          </div>

          <div className="grid md:grid-cols-2 gap-6">
            <div className="bg-white rounded-xl p-6 border border-surface-200">
              <h3 className="font-semibold text-brand-900 mb-3">What this admission means</h3>
              <p className="text-surface-600 text-sm mb-3">
                The Square Mile has one of the densest concentrations of utility infrastructure in Britain.
                The Corporation formally acknowledges that excavations shorten highway life "by a number
                of years" — documented knowledge of accelerated deterioration on specific routes.
              </p>
              <p className="text-surface-600 text-sm">
                If your defect formed after streetworks on or near your road, ask whether the Corporation
                re-inspected the carriageway post-reinstatement and whether resurfacing was deferred.
              </p>
            </div>
            <div className="bg-amber-50 border border-amber-200 rounded-xl p-6">
              <h3 className="font-semibold text-amber-900 mb-3">Questions worth asking</h3>
              <ul className="text-amber-800 text-sm space-y-2">
                <li>• Were there utility excavations on your street in the 12 months before your incident?</li>
                <li>• Was the carriageway reinstated to specification — or patched and left?</li>
                <li>• Did the Corporation accelerate resurfacing, or apply a make-safe and defer?</li>
                <li>• Was your defect on a section the CVI survey rated Red or Dark Amber?</li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* 2025/26 programme */}
      <section className="py-16 bg-white">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-display text-3xl font-bold text-brand-950 mb-4">
            2025/26 Resurfacing Programme
          </h2>
          <p className="text-surface-600 mb-8">
            Thirteen planned sections — the Corporation's own forward programme for the current financial year
          </p>

          <div className="bg-surface-50 border border-surface-200 rounded-2xl p-6 mb-8">
            <ul className="text-surface-700 text-sm space-y-2 columns-1 sm:columns-2">
              <li>• Poultry and Cheapside (Old Jewry to Bow Lane)</li>
              <li>• America Square (Crescent to Crosswall)</li>
              <li>• Finsbury Circus (in its entirety)</li>
              <li>• Cannon Street (Abchurch Lane to College Hill)</li>
              <li>• Giltspur Street (Newgate Street to West Smithfield)</li>
              <li>• Old Jewry (Lothbury to Poultry)</li>
              <li>• Watergate and Kingscote Street (New Bridge Street to Tudor Street)</li>
              <li>• Southampton Buildings (High Holborn to Chancery Lane)</li>
              <li>• Billiter Street and Fenchurch Avenue (Leadenhall Street to Fen Court)</li>
              <li>• College Hill (Cannon Street to College Street — two sections)</li>
              <li>• Crosswall (Crutched Friars to Minories)</li>
              <li>• Cornhill (Royal Exchange to Bishopsgate)</li>
            </ul>
            <p className="text-surface-500 text-xs mt-4 italic">
              Some sections may be re-scheduled and the programme is subject to change wherever possible or applicable.
            </p>
          </div>

          <div className="bg-brand-950 text-white rounded-2xl p-8">
            <h3 className="font-display text-xl font-bold mb-4">Why the programme matters for your claim</h3>
            <p className="text-brand-200 mb-4">
              On a network where the Corporation admits at least a quarter of streets need resurfacing,
              a published programme of thirteen sections is selective — not comprehensive. If your
              incident was on a street <em>not</em> listed, the Corporation's planned investment does not
              prove your carriageway was maintained to a reasonable standard.
            </p>
            <p className="text-white font-medium">
              Conversely, if your defect was on a listed section that was deferred or re-scheduled,
              that is evidence of known maintenance need that was not addressed in time.
            </p>
          </div>
        </div>
      </section>

      {/* What AMBER + GREEN spend means for claims */}
      <section className="py-16 bg-surface-50">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-display text-3xl font-bold text-brand-950 mb-4">
            Claiming Against a Well-Run AMBER Authority
          </h2>
          <p className="text-surface-600 mb-8">
            Honest assessment: the Corporation is not Derbyshire — here is how that changes your approach
          </p>

          <div className="grid md:grid-cols-2 gap-8 mb-8">
            <div className="bg-white rounded-2xl p-6 border border-surface-200">
              <h3 className="font-semibold text-brand-900 mb-4">What works in the Corporation's favour</h3>
              <ul className="text-surface-700 space-y-2 text-sm">
                <li className="flex items-start gap-2">
                  <span className="text-emerald-500">✓</span> GREEN spend scorecard — invests well beyond its £70k DfT allocation
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-emerald-500">✓</span> Annual Coarse Visual Inspection on every street section
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-emerald-500">✓</span> Documented life-cycle programme and HMEP cornerstone
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-emerald-500">✓</span> Red priority share falling from 24% (2020) to 18% (2024)
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-emerald-500">✓</span> Utility streetworks co-ordination with programmed resurfacing
                </li>
              </ul>
              <p className="text-surface-500 text-sm mt-4 italic">
                Expect a well-documented Section 58 defence. Generic claims on ceremonial routes will struggle.
              </p>
            </div>

            <div className="bg-amber-50 border border-amber-200 rounded-2xl p-6">
              <h3 className="font-semibold text-amber-900 mb-4">What works in yours</h3>
              <ul className="text-amber-800 space-y-2 text-sm">
                <li className="flex items-start gap-2">
                  <span className="text-rose-500">✗</span> AMBER condition — 18% Red priority, 21% five-year average
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-rose-500">✗</span> At least a quarter of network warranting resurfacing or renewal
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-rose-500">✗</span> Other funding streams quadrupled to £494k in 2024/25
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-rose-500">✗</span> Utility works admitted to shorten highway life by years
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-rose-500">✗</span> No published pothole fill counts — only live fault data
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-rose-500">✗</span> DfT weakest condition score on U-roads (39.0 index)
                </li>
              </ul>
            </div>
          </div>

          <div className="bg-brand-950 text-white rounded-2xl p-8">
            <h3 className="font-display text-xl font-bold mb-4">The winning strategy here is specificity</h3>
            <p className="text-brand-200 mb-4">
              Against a Corporation with GREEN spend and a documented HMEP, your claim lives or dies on the{' '}
              <strong className="text-white">specific defect and street</strong>:
            </p>
            <ul className="text-brand-300 space-y-2 mb-6">
              <li>• Prior reports via the Corporation's highways fault-reporting system — proof of actual notice</li>
              <li>• Photos showing defect size, depth and visible age (weathered edges, prior patching, utility reinstatement)</li>
              <li>• CVI priority band — was your street Red or Dark Amber at the last annual survey?</li>
              <li>• Whether your section was on the 2025/26 resurfacing programme — or deferred</li>
              <li>• TfL red route check — confirm the Corporation is the correct highway authority</li>
            </ul>
            <p className="text-white font-medium">
              Mac builds exactly this case: he searches for prior reports, assesses your photo evidence, and
              cites the Corporation's own transparency data where it helps you.
            </p>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 bg-brand-950 text-white">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="font-display text-3xl font-bold mb-4">
            Hit a Pothole in the Square Mile?
          </h2>
          <p className="text-xl text-brand-200 mb-8">
            A well-documented authority demands a well-built claim. £35 for a professional claim pack.
          </p>

          <div className="grid sm:grid-cols-2 gap-6 mb-8">
            <div className="bg-brand-900/50 rounded-xl p-6 border border-brand-800">
              <h3 className="font-semibold text-brand-300 mb-3">DIY claim</h3>
              <ul className="text-sm text-brand-400 space-y-1 text-left">
                <li>• Submit photos and invoices</li>
                <li>• Use generic template letter</li>
                <li>• No CVI priority-band argument</li>
                <li>• No utility streetworks analysis</li>
                <li>• No TfL red-route check</li>
              </ul>
            </div>
            <div className="bg-accent-500/20 rounded-xl p-6 border border-accent-500/30">
              <h3 className="font-semibold text-white mb-3">Professional claim pack</h3>
              <ul className="text-sm text-brand-200 space-y-1 text-left">
                <li>✅ 18% Red priority and £494k spend spike cited</li>
                <li>✅ Utility-works life-shortening admission argued</li>
                <li>✅ 2025/26 resurfacing programme cross-checked</li>
                <li>✅ Prior fault reports searched and attached</li>
                <li>✅ Section 58 rebuttal tailored to the Corporation</li>
              </ul>
            </div>
          </div>

          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link
              to="/check?council=city-of-london"
              className="inline-flex items-center justify-center px-8 py-4 bg-accent-500 text-white font-semibold rounded-xl hover:bg-accent-600 transition-all hover:shadow-lg hover:shadow-accent-500/25"
            >
              Order My City of London Claim Pack — £35
              <svg className="ml-2 w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 8l4 4m0 0l-4 4m4-4H3" />
              </svg>
            </Link>
            <a
              href="https://www.cityoflondon.gov.uk/services/streets/activities-on-the-highway/highways-fault-reporting"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center justify-center px-8 py-4 bg-white/10 text-white font-semibold rounded-xl hover:bg-white/20 transition-colors"
            >
              Report a pothole to the Corporation
            </a>
          </div>

          <p className="text-brand-400 text-sm mt-6">
            No percentage fees. You keep 100% of any compensation.
          </p>
        </div>
      </section>

      {/* FAQs */}
      <section className="py-16 bg-surface-50">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-display text-2xl font-bold text-brand-950 mb-8">
            Frequently Asked Questions
          </h2>

          <div className="space-y-6">
            {faqItems.map((faq) => (
              <div key={faq.question} className="bg-white rounded-xl p-6 border border-surface-200">
                <h3 className="font-semibold text-brand-900 mb-2">{faq.question}</h3>
                <p className="text-surface-600 text-sm">{faq.answer}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Attribution Footer */}
      <section className="py-8 bg-surface-100 border-t border-surface-200">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <p className="text-xs text-surface-500 text-center">
            Data sources:{' '}
            <a
              href="https://www.gov.uk/government/publications/local-road-maintenance-ratings/local-road-maintenance-ratings-2025-to-2026"
              target="_blank"
              rel="noopener noreferrer"
              className="text-brand-600 hover:underline"
            >
              Department for Transport — Local Road Maintenance Ratings 2025 to 2026
            </a>{' '}
            |{' '}
            <a
              href="https://www.cityoflondon.gov.uk/services/streets/activities-on-the-highway/highway-maintenance-and-operations"
              target="_blank"
              rel="noopener noreferrer"
              className="text-brand-600 hover:underline"
            >
              City of London Corporation — Highway maintenance and operations transparency report (updated 6 May 2026)
            </a>
            . Contains public sector information licensed under the{' '}
            <a
              href="https://www.nationalarchives.gov.uk/doc/open-government-licence/version/3/"
              target="_blank"
              rel="noopener noreferrer"
              className="text-brand-600 hover:underline"
            >
              Open Government Licence v3.0
            </a>
            .
          </p>
        </div>
      </section>
    </div>
  );
}
